import { ICONE_DEFAULT, ICONES } from "./ui/icons";
import { HOTKEY_PADRAO } from "./ui/hotkey";
import type { AltBugConfig, ConfigRemota, Posicao, Reporter, Tema } from "./types";

const POSICOES: Posicao[] = ["bottom-right", "bottom-left", "top-right", "top-left"];
const TEMAS: Tema[] = ["light", "dark", "auto"];

const DEFAULTS = {
  posicao: "bottom-right" as Posicao,
  consoleHabilitado: true,
  consoleMaxLinhas: 100,
  // Rótulos das duas entradas do menu. São DEFAULTS: o time os sobrescreve na config do
  // relator, e o servidor manda `null` quando não configurou — é o null que autoriza estes.
  texto: "Reportar bug",
  rotuloSecundario: "Abrir chamado",
  tema: "auto" as Tema,
  cor: "#2f6df6",
  icone: ICONE_DEFAULT as string,
  // Aceitar anônimo é o comportamento de sempre; recusar é a escolha nova, e ela chega
  // pelo servidor. Se o `GET /config` falhar (offline/CORS), o widget não passa a barrar
  // ninguém — best-effort não pode virar gate.
  aceitaAnonimo: true,
  // Exigir login é o oposto: nasce DESLIGADO, porque o bundle vive no HTML de terceiros e
  // não se atualiza com o nosso deploy — ligar por default trocaria o report de todo site
  // já embarcado por uma tela de login sem ninguém ter pedido.
  exigeLogin: false,
};

/** Aceita "#rgb" | "#rrggbb" (com ou sem #); devolve normalizado com "#" ou o default. */
function cor(v: string | undefined): string {
  const s = v?.trim().replace(/^#/, "");
  return s && /^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s) ? `#${s}` : DEFAULTS.cor;
}

function icone(v: string | undefined): string {
  return v && v in ICONES ? v : DEFAULTS.icone;
}

function inteiro(v: string | undefined, fallback: number): number {
  const n = v != null ? Number.parseInt(v, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function posicao(v: string | undefined): Posicao {
  return v && (POSICOES as string[]).includes(v) ? (v as Posicao) : DEFAULTS.posicao;
}

function tema(v: string | undefined): Tema {
  return v && (TEMAS as string[]).includes(v) ? (v as Tema) : DEFAULTS.tema;
}

/** Tetos do contexto do host (DEV-268). Os MESMOS do servidor (`App\Support\ContextoDoHost`):
    o widget corta por cortesia com quem embarca — a autoridade continua sendo o intake, que
    normaliza de novo porque o payload chega de um POST anônimo. */
const CTX_MAX_CHAVES = 10;
const CTX_MAX_CHAVE = 40;
const CTX_MAX_VALOR = 200;

/** Colapsa espaço (quebra de linha inclusive) e corta no teto: cada par vira uma LINHA de
    lista no `contexto.md` do card, e um `\n` no meio do valor partiria a lista em duas. */
function limpar(texto: string, max: number): string {
  return texto.replace(/\s+/g, " ").trim().slice(0, max);
}

/**
 * O contexto que a aplicação anfitriã declara sobre si mesma (DEV-268), normalizado.
 *
 * Aceita valor escalar e o converte — `versao: 3` é coisa razoável de um host mandar, e
 * recusar por tipo seria pedantismo sobre um objeto que ele escreve à mão. Aninhado cai:
 * isto é mapa RASO, e achatá-lo inventaria uma forma que ninguém pediu.
 *
 * `undefined` quando não sobra nada: o servidor grava `null` para "o host não declarou", e
 * mandar `{}` seria afirmar que ele declarou o vazio.
 */
function contexto(bruto: unknown, versaoDoDataset?: string): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  const versao = limpar(versaoDoDataset ?? "", CTX_MAX_VALOR);
  // O atalho `data-altbug-versao` é só açúcar para a chave `versao` — um caminho, não dois.
  // Ele existe porque o `data-*` é o modo cola-e-esquece (auto-init, sem `AltBug.init`), e
  // o caso mais comum do card cabe inteiro num atributo.
  if (versao !== "") out.versao = versao;

  if (bruto && typeof bruto === "object" && !Array.isArray(bruto)) {
    for (const [chave, valor] of Object.entries(bruto as Record<string, unknown>)) {
      if (Object.keys(out).length >= CTX_MAX_CHAVES) break;
      if (valor === null || typeof valor === "object" || typeof valor === "function") continue;
      const c = limpar(chave, CTX_MAX_CHAVE);
      const v = limpar(String(valor), CTX_MAX_VALOR);
      if (c === "" || v === "") continue;
      out[c] = v;
    }
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

/** Reporter vindo dos data-attrs (`data-altbug-reporter-nome/-email`); undefined
    se nenhum foi passado. O override do init() tem prioridade sobre isto. */
function reporterDeDataset(d: DOMStringMap): Reporter | undefined {
  const nome = d.altbugReporterNome?.trim();
  const email = d.altbugReporterEmail?.trim();
  if (!nome && !email) return undefined;
  return { ...(nome ? { nome } : {}), ...(email ? { email } : {}) };
}

/**
 * Endpoint padrão do intake quando `data-altbug-endpoint` é omitido: a MESMA
 * origem de onde o bundle foi carregado + `/intake/v1/bugs`. Como o backend serve
 * o bundle em `APP_URL/relator/v1/altbug.js`, isto resolve para
 * `APP_URL/intake/v1/bugs` automaticamente — sem baking no build nem env por ambiente.
 * Vazio se o src não for uma URL http(s) utilizável (ex.: file://).
 */
function endpointPadrao(scriptSrc: string | undefined): string {
  if (!scriptSrc) return "";
  try {
    const u = new URL(scriptSrc);
    return u.protocol === "http:" || u.protocol === "https:" ? `${u.origin}/intake/v1/bugs` : "";
  } catch {
    return "";
  }
}

/**
 * Resolve a config final a partir dos `data-*` do <script> e de um override
 * opcional (AltBug.init). O override vence os data-attrs. `data-altbug-endpoint`
 * é OPCIONAL: sem ele, o intake é derivado da origem do próprio `<script src>`.
 *
 * **O que o HOST configura, e por quê só isso** (DEV-144). Ficou aqui o que o relator
 * não tem como saber: `token` (é ele que identifica o relator), `endpoint` (derivado do
 * `<script src>`; só se declara quando o bundle está num CDN), `reporter` (quem está
 * logado NAQUELA página, em runtime), `contexto` (qual build está rodando ali — DEV-268) e
 * o comportamento operacional da página — `position`, `theme`, `hotkey`, `console-max`.
 *
 * Aparência e regra saíram: cor, ícone, captura de console e o rótulo do botão são do
 * TIME, e chegam por `GET /intake/v1/config`. Antes eram dos dois, com uma máquina de
 * precedência para arbitrar — que sumiu junto, porque não havia como distinguir "o host
 * escolheu o default" de "o host não escolheu nada" (a mesma string nos dois casos).
 *
 * data-altbug-token (obrigatório), data-altbug-endpoint, data-altbug-position,
 * data-altbug-console-max, data-altbug-reporter-nome, data-altbug-reporter-email,
 * data-altbug-versao (atalho para `contexto.versao`, DEV-268),
 * data-altbug-hotkey (presente/"true" → FAB oculto até "?"; desde o DEV-222 é também a
 *   única forma de o launcher SUMIR de vez — sem a flag, "Esconder" o encolhe num talo
 *   tocável em vez de tirá-lo da tela),
 * data-altbug-theme ("light" | "dark" | "auto"; default "auto").
 */
export function resolverConfig(
  dataset: DOMStringMap | undefined,
  override?: Partial<AltBugConfig>,
  scriptSrc?: string,
): AltBugConfig {
  const d = dataset ?? {};
  return {
    token: override?.token ?? d.altbugToken ?? "",
    endpoint: override?.endpoint ?? d.altbugEndpoint ?? endpointPadrao(scriptSrc),
    posicao: override?.posicao ?? posicao(d.altbugPosition),
    consoleMaxLinhas: override?.consoleMaxLinhas ?? inteiro(d.altbugConsoleMax, DEFAULTS.consoleMaxLinhas),
    reporter: override?.reporter ?? reporterDeDataset(d),
    // O contexto do host FUNDE as duas fontes em vez de uma vencer a outra: o
    // `data-altbug-versao` está no `<script>` (o build, que o deploy carimba) e o objeto do
    // `init` está em runtime (o que a aplicação sabe de si). São perguntas diferentes, e
    // fazer uma apagar a outra perderia dado que ninguém pediu para perder — o que o `init`
    // manda em `versao` é que sobrepõe o atalho, porque aí são a MESMA chave.
    contexto: contexto(override?.contexto, d.altbugVersao),
    hotkey: override?.hotkey ?? (d.altbugHotkey != null && d.altbugHotkey !== "false"),
    hotkeyCombo: override?.hotkeyCombo ?? d.altbugHotkeyCombo ?? HOTKEY_PADRAO,
    tema: override?.tema ?? tema(d.altbugTheme),
    // Daqui para baixo, tudo é do SERVIDOR: o que fica aqui é só o valor até o
    // `GET /config` responder (ou para sempre, se ele falhar e não houver cache).
    consoleHabilitado: DEFAULTS.consoleHabilitado,
    texto: DEFAULTS.texto,
    cor: DEFAULTS.cor,
    icone: DEFAULTS.icone,
    aceitaAnonimo: DEFAULTS.aceitaAnonimo,
    exigeLogin: DEFAULTS.exigeLogin,
    tiposAdicionais: [],
    rotuloSecundario: DEFAULTS.rotuloSecundario,
  };
}

/**
 * Funde a config remota (do relator, buscada no intake) na config local — muta `cfg`.
 *
 * Não há mais precedência a arbitrar (DEV-144): o que está aqui é do time, ponto. Cor e
 * ícone ainda passam pelos validadores porque o valor vem de um JSON de rede — servidor
 * com dado corrompido cai no default em vez de pintar o botão de nada.
 */
export function mesclarConfigRemota(cfg: AltBugConfig, remota: ConfigRemota): void {
  if (typeof remota.cor === "string") cfg.cor = cor(remota.cor);
  if (typeof remota.icone === "string") cfg.icone = icone(remota.icone);
  if (typeof remota.consoleHabilitado === "boolean") cfg.consoleHabilitado = remota.consoleHabilitado;
  if (typeof remota.aceitaAnonimo === "boolean") cfg.aceitaAnonimo = remota.aceitaAnonimo;
  if (typeof remota.exigeLogin === "boolean") cfg.exigeLogin = remota.exigeLogin;
  if (typeof remota.portalUrl === "string" && remota.portalUrl !== "") cfg.portalUrl = remota.portalUrl;
  // Rótulos: string vazia não conta (o servidor manda `null` quando não configurado, mas
  // o widget não deve depender disso para não ficar com um botão sem texto).
  if (typeof remota.rotuloPrincipal === "string" && remota.rotuloPrincipal.trim() !== "") {
    cfg.texto = remota.rotuloPrincipal.trim();
  }
  if (typeof remota.rotuloSecundario === "string" && remota.rotuloSecundario.trim() !== "") {
    cfg.rotuloSecundario = remota.rotuloSecundario.trim();
  }
  if (Array.isArray(remota.tiposAdicionais)) cfg.tiposAdicionais = remota.tiposAdicionais;
}
